import { isRef, ref, reactive, onMounted, computed, watch, type Ref} from "vue";
import type { MaybeRef } from "@vueuse/core";
import BuilderApi from "@/helpers/api/Builder";
import { syncRef } from "@vueuse/core";
import { usePage } from "@inertiajs/vue3";
export default function useBuilder({
    builder,
    isBuilderPage,
    isBuilderSection,
    isBuilderData,
    url,
    fallBackUrl,
    readableFields,
    model,
    search,
    limit,
    data,
    lazy,
    contentProp
}: {
    builder: BuilderApi;
    isBuilderPage: boolean;
    isBuilderSection: boolean;
    isBuilderData: boolean;
    url: string;
    fallBackUrl: string;
    readableFields: string[];
    model: string;
    search?: MaybeRef<Object>;
    limit?: MaybeRef<number>; 
    data?: Ref,
    lazy?: Ref<boolean>;
    contentProp: string;
}) {
    const state = reactive({
        canShowContent: contentProp && !builder.isPreviewing() ? usePage().props[contentProp] != null : false,
        content: contentProp && !builder.isPreviewing() ? usePage().props[contentProp] : null
    });

    let builderData = ref([]);
    search = ref(search);

    let searchParamsWithScheduleCheck = computed(() => {
        
       let  params ={
                'startDate.$or': [
                    {
                        $exists: false
                    },
                    {
                        $eq: null
                    },
                    {
                        $lte: new Date().getTime()
                    }
                ],
                'endDate.$or': [
                    {
                        $exists: false
                    },
                    {
                        $eq: null
                    },
                    {
                        $gte: new Date().getTime()
                    }
                ]
            }
        if(!search.value || Object.keys(search.value).length == 0){
            return params
        }
        return {
            ...search.value,
            ...params
        }
    })
    let stop = null;
    if(isRef(data)){
         stop = syncRef(builderData, data);
    }
    const hasBuilderContent = computed(() => {
        return (isBuilderPage || isBuilderSection) && state.canShowContent;
    });
    const isLoadingContent = ref(false);
    async function fetchBuilderContent(){
        if ((isBuilderPage || isBuilderSection) && !hasBuilderContent.value) {
            let options = {};
            if(url != ""){
                options = {
                    userAttributes: {
                        urlPath: url
                    }
                }
            }else if(fallBackUrl != ""){
                options = {
                    userAttributes: {
                        urlPath: fallBackUrl
                    }
                }
            }
            try {
                isLoadingContent.value = true;
                let res = await builder.fetchOneEntry({
                    model,
                    query: searchParamsWithScheduleCheck.value,
                    options
                });
                isLoadingContent.value = false;

                state.content = res;
                state.canShowContent = state.content || builder.isPreviewing();

                if (isBuilderSection && readableFields.length > 0) {
                    const _data = {};
                    for (const field of readableFields) {
                        _data[field] = res?.data ? res?.data[field] : null;
                    }
                    builderData.value = _data;
                }
            } catch (err) {
                state.content = {
                    error: "Content not found"
                }
                state.canShowContent = true
            }
        }else if((isBuilderSection || isBuilderPage) && hasBuilderContent.value){
           state.canShowContent = state.content || builder.isPreviewing();
            if(isBuilderSection && readableFields.length > 0){
                const _data = {};
                for (const field of readableFields) {
                    _data[field] = state.content?.data ? state.content?.data[field] : null;
                }
                builderData.value = _data;
            }else if(isBuilderPage && readableFields.length > 0){
                const _data = {};
                for (const field of readableFields) {
                    _data[field] = state.content?.data ? state.content?.data[field] : null;
                }
                builderData.value = _data;
            }
        }
        
        if (isBuilderData) {
            try {
                let totalResponse = [];
                if(limit.value > 100){
                    let total = limit.value;

                    while(total > 0){
                        let res =
                            (await builder?.fetchEntries?.({
                                model,
                                query: searchParamsWithScheduleCheck.value,
                                options: {
                                    limit: total > 100 ? 100 : total,
                                    offset: (limit.value - total) > 0 ? (limit.value - total) : 0
                                }
                            })) ?? [];
                        totalResponse.push(...res);
                        total -= 100;
                    }
                    builderData.value = totalResponse.map((entry) => ({
                        ...entry.data,
                        builderId: entry.id
                    }));
                }else{
                    let res =
                        (await builder?.fetchEntries?.({
                            model,
                            query: searchParamsWithScheduleCheck.value,
                            options: {
                                limit: limit.value ?? 30
                            }
                        })) ?? [];
                    builderData.value = res.map((entry) => ({
                        ...entry.data,
                        builderId: entry.id
                    }));
                }
            } catch (err) {
            }
        }
    }

    onMounted(async () => {
        if(!lazy.value){
            try{
                await fetchBuilderContent();
            }catch(err){
                // console.error(err);
            }
        }

        watch(
            searchParamsWithScheduleCheck,
            async (newSearch) => {
                if (isBuilderData) {
                    try {
                        if(limit.value > 100){
                            let total = limit.value;
                            let totalResponse = [];
                            while(total > 0){
                                let res =
                                    (await builder?.fetchEntries?.({
                                        model,
                                        query: newSearch,
                                        options: {
                                            limit: total > 100 ? 100 : total,
                                            offset: (limit.value - total) > 0 ? (limit.value - total) : 0
                                        }
                                    })) ?? [];
                                totalResponse.push(...res);
                                total -= 100;
                            }
                            builderData.value = totalResponse.map((entry) => ({
                                ...entry.data,
                                builderId: entry.id
                            }));
                        }else{
                            const res =
                                (await builder?.fetchEntries?.({
                                    model,
                                    query: newSearch,
                                    options: {
                                        limit: limit.value ?? 30
                                    }
                                })) ?? [];
                            builderData.value = res.map((entry) => entry.data);
                        }
                        
                    } catch (err) {
                        console.error(err);
                    }
                }
            },
            {
                deep: true
            }
        );
        

        watch(
            limit,
            async (newLimit) => {
                if (isBuilderData) {
                   
                    try {
                        if(newLimit > 100){
                            let total = newLimit;
                            let totalResponse = [];
                            while(total > 0){
                            let res =
                                (await builder?.fetchEntries?.({
                                    model,
                                    query: searchParamsWithScheduleCheck.value,
                                    options: {
                                        limit: total > 100 ? 100 : total,
                                        offset: (newLimit - total) > 0 ? (newLimit - total) : 0
                                    }
                                })) ?? [];
                            totalResponse.push(...res);
                            total -= 100;
                        }
                        builderData.value = totalResponse.map((entry) => ({
                            ...entry.data,
                            builderId: entry.id
                        }));
                    }else if(newLimit <= 0){
                        const res =
                            (await builder?.fetchEntries?.({
                                model,
                                query: searchParamsWithScheduleCheck?.value,
                                options: {
                                    limit: newLimit ?? 30
                                }
                            })) ?? [];
                        builderData.value = res.map((entry) => entry.data);
                       
                    }
                    } catch (err) {
                        console.error(err);
                    }
                }
            },
            {
                deep: true
            }
        );


        watch(lazy, async (newLazy) => {
            if(!newLazy){
                try{
                    await fetchBuilderContent();
                }catch(err){
                }
            }
        });
    });

    return {
        state,
        hasBuilderContent,
        data: builderData,
        isLoadingContent,
        fetchBuilderContent
    };
}
